<template>
  <v-container fluid class="mx-0 px-0">
    <template v-if="showTurtasTeise">
      <el-checkbox
        class="mb-3"
        :label="turtasTeiseLabel"
        :tooltip="`${ntrTooltipPrefix} Turtas. ${turtasTeiseTooltip}`"
        :readonly="readonlyTurtasTeise"
        v-model="formData.turtasTeise"
        @click.native="!readonlyTurtasTeise ? saveTurtas() : null"
      />
    </template>

    <prasymas-turtas-search
      v-if="showTurtasSearch"
      :pageloading="pageloading"
      :turtas-teise="formData.turtasTeise"
      :turtas-length="turtasLength"
      @updateTurtasList="updateTurtasList"
    />

    <v-data-table
      :class="tableClass"
      :headers="filteredHeaders"
      :items="itemsList"
      :loading="loading"
      :search="search"
      :item-class="rowClass"
      :footer-props="{
        showFirstLastPage: true,
        showCurrentPage: true,
        itemsPerPageText: this.$t('Rodyti po:'),
        itemsPerPageAllText: this.$t('visi'),
        itemsPerPageOptions: itemsPerPageOptions,
      }"
      :hide-default-footer="hideFooter"
      :items-per-page="pageSize"
      :expanded.sync="formData.turtai"
      :group-by="groupBy"
      :show-group-by="false"
    >
      <template #[`group.header`]="{ headers, items }">
        <td :colspan="headers.length">
          {{ items[0].grupe }}
        </td>
      </template>
      <template #[`item.registras`]="{ item }">
        <v-row dense no-gutters>
          <v-col cols="12" md="12">
            <div class="nowrap">{{ item.tur_registras }}</div>
          </v-col>
          <v-col cols="12" md="12">
            <div class="nowrap">{{ item.tur_identifikatorius }}</div>
          </v-col>
        </v-row>
      </template>
      <template #[`item.tur_plotas_format`]="{ item }">
        <template v-if="item.tur_plotas">
          <div class="nowrap">
            {{ item.tur_plotas_format }}
          </div>
          {{ item.tur_plotas_matas }}
        </template>
      </template>
      <template #[`item.rusis`]="{ item }">
        <v-row dense no-gutters>
          <v-col cols="12" md="12">
            {{ item.tur_ntr_rusis }}
          </v-col>
          <v-col cols="12" md="12">
            <span class="text-gray">{{ item.tur_ntr_adresas }}</span>
          </v-col>
        </v-row>
      </template>
      <template #[`item.actions`]="{ item }">
        <el-table-action
          :item="item"
          @editItem="editItem(item)"
          @viewItem="editItem(item)"
          @deleteItem="deleteItem(item)"
          @reloadItem="reloadItem(item)"
        />
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td class="turtas-expanded pb-4"></td>
        <td :colspan="headers.length - 1" class="turtas-expanded pb-4">
          <template v-if="item.showPastabos">
            <v-row dense no-gutters class="pt-1 pb-2">
              <v-col cols="12" md="12" class="bold-label">
                {{ $t('Pastabos:') }}
              </v-col>
              <v-col
                v-for="(klaida, klaidaIndex) in item.tur_klaidos"
                :key="`klaida${turtasRefresh}-${klaidaIndex}`"
                cols="12"
                md="12"
                class="turtas-pastaba-error mb-1"
              >
                {{ klaida }}
              </v-col>
              <v-col
                v-for="(ispejimas, ispejimasIndex) in item.ispejimai"
                :key="`ispejimas${turtasRefresh}-${ispejimasIndex}`"
                cols="12"
                md="12"
                class="turtas-pastaba-warning"
              >
                {{ ispejimas.message }}
                <span
                  class="linkas"
                  v-if="ispejimas.linkas"
                  @click.prevent.stop="gotoLinkas(ispejimas.linkas.value)"
                  >{{ ispejimas.linkas.text }}</span
                >
              </v-col>
            </v-row>
            <v-divider />
          </template>

          <template v-if="!item.turtines_teises">
            <v-row dense no-gutters>
              <v-col cols="12" md="3" class="bold-label pt-3">
                {{ $t('NT dalis')
                }}<el-tooltip
                  inline
                  :tooltip="`${ntrTooltipPrefix} Turtas. NT dalis`"
                  :tooltip-key="`ntdalis-${item.tur_id}`"
                />
              </v-col>
              <v-col cols="12" md="9">
                <div
                  v-for="dalis in item.dalys"
                  :key="`dalisdiv-${item.tur_id}-${dalis.tda_id}`"
                >
                  <el-checkbox
                    :key="`dalis-${item.tur_id}-${dalis.tda_id}`"
                    :validate-key="`dalis-${item.tur_id}-${dalis.tda_id}`"
                    :label="`${dalis.tda_pavadinimas}`"
                    :sup-label="
                      `${
                        item.dalys_pazymetos.includes(dalis.tda_id)
                          ? dalis.pastabos
                          : ''
                      }`
                    "
                    :check-value="dalis.tda_id"
                    :readonly="item.readonly"
                    v-model="item.dalys_pazymetos"
                    @click.native="saveTurtas(item, true)"
                  />
                </div>
              </v-col>
            </v-row>
          </template>

          <template v-if="item.turtines_teises">
            <v-row dense no-gutters class="mb-2">
              <v-col cols="12" md="3" class="bold-label pt-4">
                {{ item.turtasTeiseTitle
                }}<el-tooltip
                  inline
                  :tooltip="
                    `${ntrTooltipPrefix} Turtas. ${item.turtasTeiseTitle}`
                  "
                  :tooltip-key="`turtasteise-${item.tur_id}`"
                />
              </v-col>
              <v-col cols="12" md="9">
                <div
                  v-for="teise in item.teises"
                  :key="`teisediv-${item.tur_id}-${teise.tte_id}`"
                >
                  <v-row dense no-gutters>
                    <v-col cols="12" md="3" v-if="!isPrasymasSubnuoma">
                      <el-checkbox
                        :key="`dalis-${item.tur_id}-${teise.tte_id}`"
                        :label="`${teise.tda_pavadinimas}`"
                        :sup-label="
                          `${
                            item.teises_pazymetos.includes(teise.tte_id)
                              ? teise.pastabos
                              : ''
                          }`
                        "
                        :check-value="teise.tte_id"
                        :readonly="item.readonly"
                        v-model="item.teises_pazymetos"
                        @click.native="saveTurtas(item, true)"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      md="3"
                      class="mt-3"
                      v-if="isPrasymasSubnuoma"
                    >
                      {{ teise.tda_pavadinimas }}
                    </v-col>
                    <v-col cols="12" md="9">
                      <div class="text-gray mt-3">
                        <div v-if="teise.tda_pradzia">
                          {{ $t('Galioja nuo:') }} {{ teise.tda_pradzia }}
                        </div>
                        <div v-if="teise.tda_terminas">
                          {{ $t('Terminas:') }} {{ teise.tda_terminas }}
                        </div>
                        <div v-if="teise.tda_dokumentas">
                          {{ teise.tda_dokumentas }}
                        </div>
                      </div>
                      <div v-if="teise.isnuomota_dalis">
                        {{ isnuomotaDalisPavadinimas(teise.tda_pavadinimas) }}
                        {{ teise.isnuomota_dalis }}
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>
          </template>

          <template
            v-if="
              (isPrasymasNuoma ||
                isPrasymasPanauda ||
                isPrasymasSubnuoma ||
                isPrasymasPreliminari) &&
                isPrasymasIregistravimas
            "
          >
            <v-divider v-if="isPrasymasSubnuoma || isPrasymasPreliminari" />
            <v-row
              dense
              no-gutters
              v-if="
                isPrasymasNuoma || isPrasymasSubnuoma || isPrasymasPreliminari
              "
            >
              <v-col
                :class="`bold-label pt-4`"
                cols="12"
                :md="`${labelColumnFix}`"
                align-self="center"
              >
                {{ isnuomotaSumaLabel
                }}<el-need v-if="!item.readonly" /><el-tooltip
                  inline
                  :tooltip="
                    `${ntrTooltipPrefix} Turtas. ${isnuomotaSumaTooltip}`
                  "
                  :tooltip-key="`nuomossuma-${item.tur_id}`"
                />
              </v-col>
              <v-col cols="12" md="4">
                <el-money-field
                  :readonly="item.readonly"
                  :key="`tur_suma${item.tur_id}`"
                  hide-details="auto"
                  :precision="2"
                  :validate-key="`tur_suma${item.tur_id}`"
                  v-model="item.tur_suma"
                  @blur="saveTurtas(item)"
                />
              </v-col>
              <v-col cols="12" md="3" v-if="isPrasymasPreliminari">
                <el-checkbox
                  class="mt-4 ml-4"
                  label="Įskaičiuotas PVM"
                  :tooltip="`${ntrTooltipPrefix} Turtas. Įskaičiuotas PVM`"
                  :readonly="item.readonly"
                  v-model="item.tur_iskaiciuotas_pvm"
                  @click.native="saveTurtas(item)"
                />
              </v-col>
            </v-row>
            <v-row
              dense
              no-gutters
              v-if="
                item.tur_plotas_pazymetas &&
                  (isPrasymasNuoma ||
                    isPrasymasPanauda ||
                    isPrasymasSubnuoma ||
                    isPrasymasPreliminari)
              "
            >
              <v-col
                class="bold-label pt-4"
                cols="12"
                :md="`${labelColumnFix}`"
                align-self="center"
              >
                {{ priklausantisPlotasLabel
                }}<el-tooltip
                  inline
                  :tooltip="
                    `${ntrTooltipPrefix} Turtas. ${priklausantisPlotasTooltip}`
                  "
                  :tooltip-key="`priklausantisplotas-${item.tur_id}`"
                />
              </v-col>
              <v-col cols="12" md="3">
                <el-money-field
                  :readonly="true"
                  hide-details="auto"
                  :validate-key="`tur_plotas_pazymetas${item.tur_id}`"
                  :precision="item.tur_plotas_precision"
                  v-model="item.tur_plotas_pazymetas"
                >
                  <template slot="append-outer">
                    <div class="plotas-matas mt-2 nowrap">
                      {{ item.tur_plotas_matas }}
                    </div>
                  </template>
                </el-money-field>
              </v-col>
            </v-row>
            <v-row
              dense
              no-gutters
              v-if="item.tur_plotas && !isPrasymasPreliminari"
            >
              <v-col
                class="bold-label pt-4"
                cols="12"
                :md="`${labelColumnFix}`"
                align-self="center"
              >
                {{ isnuomotasPlotasLabel
                }}<el-need
                  v-if="!item.readonly && item.tur_plotas"
                /><el-tooltip
                  inline
                  :tooltip="
                    `${ntrTooltipPrefix} Turtas. ${isnuomotasPlotasTooltip}`
                  "
                  :tooltip-key="`isnuomotasplotas-${item.tur_id}`"
                />
              </v-col>
              <v-col cols="12" md="3">
                <el-money-field
                  :readonly="item.readonly || !item.tur_plotas"
                  hide-details="auto"
                  :precision="item.tur_plotas_precision"
                  :key="`tur_plotas_isnuomotas${item.tur_id}`"
                  :validate-key="`tur_plotas_isnuomotas${item.tur_id}`"
                  v-model="item.tur_plotas_isnuomotas"
                  @blur="saveTurtas(item)"
                >
                  <template slot="append-outer">
                    <div class="plotas-matas mt-2 nowrap">
                      {{ item.tur_plotas_matas }}
                    </div>
                  </template>
                </el-money-field>
              </v-col>
            </v-row>

            <v-row dense no-gutters class="mt-5 pt-3">
              <v-col cols="12" md="12">
                <el-textarea
                  :readonly="item.readonly"
                  class="no-color-label"
                  :label="isnuomotasPlotasPastabaLabel"
                  :tooltip="
                    `${ntrTooltipPrefix} Turtas. ${isnuomotasPlotasPastabaTooltip}`
                  "
                  :tooltip-key="`plotaspastaba-${item.tur_id}`"
                  hide-details="auto"
                  counter="1000"
                  :key="`tur_plotas_pastaba${item.tur_id}`"
                  :validate-key="`tur_plotas_pastaba${item.tur_id}`"
                  v-model="item.tur_plotas_pastaba"
                  @blur="saveTurtas(item)"
                />
              </v-col>
            </v-row>
          </template>
        </td>
      </template>
      <template slot="loading">
        {{ $t('Kraunasi...') }}
      </template>
      <template slot="no-results">
        {{ $t('Duomenų nėra') }}
      </template>
      <template slot="no-data">
        {{ $t('Duomenų nėra') }}
      </template>
      <template #[`footer.page-text`]="props">
        {{ $t('Rodoma') }} {{ props.pageStart }}-{{ props.pageStop }}
        {{ $t('iš') }}
        {{ props.itemsLength }}
      </template>
    </v-data-table>

    <modal-delete
      :title="$t('Šalinti turtą?')"
      :text="
        $t('Ar norite pašalinti turtą') +
          ` <strong>${modalDeleteVardas}</strong>?`
      "
      v-model="showDelete"
      :item="modalDeleteItem"
      @confirmDelete="confirmDelete"
    />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import PrasymasTurtasSearch from './PrasymasTurtasSearch'
import ModalDelete from '../modals/ModalDelete'
import rowClass from '@/mixins/rowClass'
import busena from '@/mixins/busenaNtr'
import userRole from '@/mixins/userRole'
import math from '@/mixins/math'
import sklypasLinkas from '@/mixins/sklypasLinkas'
import tooltipNtr from '@/mixins/tooltipNtr'
import checkErrors from '@/mixins/checkErrors'

export default {
  name: 'PrasymasTurtas',
  components: {
    PrasymasTurtasSearch,
    ModalDelete,
  },
  props: {
    readonly: { type: Boolean, default: false },
  },
  mixins: [
    busena,
    rowClass,
    userRole,
    math,
    tooltipNtr,
    checkErrors,
    sklypasLinkas,
  ],
  data() {
    return {
      pageloading: true,
      loading: true,
      search: null,
      showDelete: false,
      modalDeleteVardas: '',
      modalDeleteItem: null,
      modalDeleteRole: null,
      turtai: [],
      salys: [],
      turtasRefresh: 0,
      itemsPerPageOptions: [10, 50, -1],
      pageSize: 10,
      expanded: [],
      formData: {
        turtai: [],
        turtasTeise: false,
      },
    }
  },
  computed: {
    ...mapGetters(['prasymasID', 'prasymasBusena', 'prasymasTipas']),
    headers() {
      return [
        {
          text: this.$t('Eil. Nr.'),
          align: 'start',
          value: 'eilnr',
          width: 50,
        },
        {
          text: this.$t('Registro informacija'),
          align: 'start',
          value: 'registras',
          width: 150,
        },
        {
          text: this.$t('Plotas'),
          align: 'start',
          value: 'tur_plotas_format',
          width: 50,
          hide:
            !this.isPrasymasNuoma &&
            !this.isPrasymasPanauda &&
            !this.isPrasymasSubnuoma &&
            !this.isPrasymasPreliminari,
        },
        {
          text: this.$t('Naudojimo paskirtis'),
          align: 'start',
          value: 'tur_paskirtis',
          width: 100,
          hide:
            !this.isPrasymasNuoma &&
            !this.isPrasymasPanauda &&
            !this.isPrasymasSubnuoma &&
            !this.isPrasymasPreliminari,
        },
        {
          text: this.$t('Turto rūšis'),
          align: 'start',
          value: 'rusis',
        },
        {
          text: '',
          align: 'center',
          sortable: false,
          value: 'actions',
          width: 50,
        },
      ]
    },
    filteredHeaders() {
      return this.headers.filter(item => {
        return !item.hide
      })
    },
    itemsList() {
      return this.turtai.map((item, index) => {
        item.eilnr = `${index + 1}.`

        item.grupenr = item.tur_pasalintas ? 1 : 2
        item.grupe = item.tur_pasalintas
          ? this.$t(
              'Daikto, kuriam išregistruojamas juridinis faktas, duomenys:'
            )
          : this.$t(
              'Daikto, kuriam įregistruojamas juridinis faktas, duomenys:'
            )

        if (item.turtines_teises) {
          item.turtasTeiseTitle = this.$t('Teisės duomenys')
        }

        item.showPastabos =
          item.tur_klaidos.length > 0 || item.tur_ispejimai.length > 0

        item.ispejimai = item.tur_ispejimai.map(ispejimas => {
          const linkas = this.parseLinkas(ispejimas)
          const message = this.cleanMessage(ispejimas)
          return {
            message: message,
            linkas: linkas ?? null,
          }
        })

        item.tur_plotas_format = item.tur_plotas
          ? item.tur_plotas.replace(item.tur_plotas_matas, '')
          : null

        item.tur_plotas_precision = 2
        if (item.tur_plotas_matas === 'ha') {
          item.tur_plotas_precision = 4
        }

        item.readonly = item.tur_pasalintas || this.isReadonly
        item.canEdit = false
        item.canView = false
        item.canDelete =
          !this.isReadonly &&
          !item.readonly &&
          !(this.isPrasymasSeima && this.isGrazintas) &&
          !(this.isPrasymasNuoma && !this.isPrasymasIregistravimas) &&
          !(this.isPrasymasPanauda && !this.isPrasymasIregistravimas)
        item.canReload = item.tur_pasikeite

        return item
      })
    },
    groupBy() {
      return this.isPrasymasPerkelimas ? 'grupenr' : null
    },
    hideFooter() {
      return this.itemsList.length <= 10
    },
    isReadonly() {
      return (
        this.readonly ||
        (this.isPrasymasNuoma && !this.isPrasymasIregistravimas) ||
        (this.isPrasymasPanauda && !this.isPrasymasIregistravimas)
      )
    },
    showTurtasSearch() {
      return (
        !this.isReadonly &&
        !this.isGrazintas &&
        !(this.isPrasymasNuoma && !this.isPrasymasIregistravimas) &&
        !(this.isPrasymasPanauda && !this.isPrasymasIregistravimas) &&
        !(this.isPrasymasSubnuoma && !this.isPrasymasIregistravimas) &&
        !(this.isPrasymasPreliminari && !this.isPrasymasIregistravimas)
      )
    },
    tableClass() {
      return 'prasymas-turtas-table'
    },
    isnuomotaSumaLabel() {
      if (this.isPrasymasSubnuoma) {
        return this.$t('Subnuomos suma per mėnesį, EUR')
      }
      if (this.isPrasymasPreliminari) {
        return this.$t('Pardavimo kaina, EUR')
      }
      return this.$t('Nuomos suma per mėnesį, EUR')
    },
    labelColumnFix() {
      if (this.isPrasymasPanauda) {
        return '5'
      }
      if (this.isPrasymasSubnuoma) {
        return '5'
      }
      return '4'
    },
    isnuomotaSumaTooltip() {
      if (this.isPrasymasSubnuoma) {
        return 'Subnuomos suma per mėnesį, EUR'
      }
      if (this.isPrasymasPreliminari) {
        return this.$t('Pardavimo kaina, EUR')
      }
      return 'Nuomos suma per mėnesį, EUR'
    },
    priklausantisPlotasLabel() {
      if (this.isPrasymasSubnuoma) {
        return this.$t('Maksimalus leistinas subnuomoti plotas')
      }
      if (this.isPrasymasPreliminari) {
        return this.$t('Priklausantis dalies plotas')
      }
      return this.$t('Priklausantis dalies(-ių) plotas')
    },
    priklausantisPlotasTooltip() {
      if (this.isPrasymasSubnuoma) {
        return 'Maksimalus leistinas subnuomoti plotas'
      }
      if (this.isPrasymasPreliminari) {
        return this.$t('Priklausantis dalies plotas')
      }
      return 'Priklausantis dalies plotas'
    },
    isnuomotasPlotasLabel() {
      if (this.isPrasymasPanauda) {
        return this.$t('Suteikiamos panaudai objekto dalies plotas')
      }
      if (this.isPrasymasSubnuoma) {
        return this.$t('Subnuomotas plotas')
      }
      return this.$t('Išnuomotas plotas')
    },
    isnuomotasPlotasTooltip() {
      if (this.isPrasymasPanauda) {
        return 'Suteikiamos panaudai objekto dalies plotas'
      }
      if (this.isPrasymasSubnuoma) {
        return 'Subnuomotas plotas'
      }
      return 'Išnuomotas plotas'
    },
    isnuomotasPlotasPastabaLabel() {
      if (this.isPrasymasPanauda) {
        return this.$t('Suteikiamos panaudai objekto dalies ploto aprašymas')
      }
      if (this.isPrasymasSubnuoma) {
        return this.$t('Subnuomoto ploto pastaba')
      }
      if (this.isPrasymasPreliminari) {
        return this.$t('Parduodamo nekilnojamojo daikto (dalies) aprašymas')
      }
      return this.$t('Išnuomoto ploto pastaba')
    },
    isnuomotasPlotasPastabaTooltip() {
      if (this.isPrasymasPanauda) {
        return 'Suteikiamos panaudai objekto dalies ploto aprašymas'
      }
      if (this.isPrasymasSubnuoma) {
        return 'Subnuomoto ploto pastaba'
      }
      if (this.isPrasymasPreliminari) {
        return this.$t('Parduodamo nekilnojamojo daikto (dalies) aprašymas')
      }
      return 'Išnuomoto ploto pastaba'
    },
    showTurtasTeise() {
      return (
        (this.isPrasymasSeima && this.isPrasymasIregistravimas) ||
        (this.isPrasymasSeima && this.isPrasymasPerkelimas && this.yraTurtas)
      )
    },
    turtasTeiseLabel() {
      if (this.isPrasymasPerkelimas) {
        return this.$t(
          'Šeimos turtas perkeliamas į turtinę nuomos / panaudos / subnuomos teisę'
        )
      }
      return this.$t(
        'Šeimos turtas registruojamas į turtinę nuomos / panaudos / subnuomos teisę'
      )
    },
    turtasTeiseTooltip() {
      if (this.isPrasymasPerkelimas) {
        return 'Šeimos turtas perkeliamas į turtinę nuomos / panaudos / subnuomos teisę'
      }
      return 'Šeimos turtas registruojamas į turtinę nuomos / panaudos / subnuomos teisę'
    },
    readonlyTurtasTeise() {
      return (
        this.isReadonly ||
        (this.isPrasymasSeima &&
          this.isPrasymasIregistravimas &&
          this.turtai.length > 0) ||
        (this.isPrasymasSeima &&
          this.isPrasymasPerkelimas &&
          this.turtai.length > 1)
      )
    },
    turtasLength() {
      return this.itemsList.length
    },
    yraTurtas() {
      return this.turtasLength > 0
    },
  },
  created() {
    this.getData()
  },
  methods: {
    getData() {
      this.pageloading = true
      this.loading = true
      this.$axios
        .get(`ntrpaslaugos/prasymas/${this.prasymasID}/turtas`)
        .then(res => {
          this.turtai = res.data.turtai
          this.formData.turtai = this.itemsList
          this.formData.turtasTeise = res.data.pra_turtas_teise
          // this.expanded = this.itemsList
          this.updateErrors()
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
          this.pageloading = false
        })
    },
    saveTurtas(item, reload = false) {
      if (this.isReadonly) {
        return
      }

      const data = {
        pra_id: this.prasymasID,
        pra_turtas_teise: this.formData.turtasTeise,
        turtai: item
          ? [
              {
                tur_id: item.tur_id,
                dalys_pazymetos: item.dalys_pazymetos,
                teises_pazymetos: item.teises_pazymetos,
                tur_suma: item.tur_suma,
                tur_plotas_isnuomotas: item.tur_plotas_isnuomotas,
                tur_plotas_pastaba: item.tur_plotas_pastaba,
                tur_iskaiciuotas_pvm: item.tur_iskaiciuotas_pvm,
              },
            ]
          : [],
      }

      this.$axios
        .post(`ntrpaslaugos/prasymas/${this.prasymasID}/turtas`, data)
        .then(res => {
          if (reload) {
            this.updateTurtasList()
          }
          // this.turtasRefresh++
          this.updateErrors()
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    deleteItem(item) {
      this.modalDeleteVardas = item.tur_identifikatorius
      this.modalDeleteItem = Object.assign({}, item)
      this.showDelete = true
    },
    confirmDelete(item) {
      this.confirmDeleteTurtas(item)
    },
    confirmDeleteTurtas(item) {
      this.$axios
        .delete(
          `ntrpaslaugos/prasymas/${this.prasymasID}/turtas/${item.tur_id}`
        )
        .then(res => {
          window._VMA.$emit('DATA_OK', res.data.message)
          this.updateTurtasList()
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
          this.pageloading = false
        })
    },
    reloadItem(item) {
      this.$axios
        .post(
          `ntrpaslaugos/prasymas/${this.prasymasID}/turtas/${item.tur_id}/atnaujinimas`
        )
        .then(res => {
          window._VMA.$emit('DATA_OK', res.data.message)
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
          this.pageloading = false
          this.updateTurtasList()
        })
    },
    updateTurtasList() {
      this.getData()
    },
    nuomosPastaba(item) {
      item.showNuomosPastaba = true
    },
    rowClass(item) {
      if (
        item.tur_pasalintas &&
        this.isPrasymasSeima &&
        this.isPrasymasPerkelimas
      ) {
        return `row-deleted`
      }
      return ''
    },
    isnuomotaDalisPavadinimas(teisePavadinimas) {
      if (teisePavadinimas === 'Subnuomos teisė') {
        return this.$t('Subnuomota NT dalis:')
      }
      if (teisePavadinimas === 'Panaudos teisė') {
        return this.$t('Panaudai suteikta NT dalis:')
      }
      return this.$t('Išnuomota NT dalis:')
    },
  },
}
</script>
<style lang="scss" scoped>
$errorHigh: #f11a1a;
$errorMiddle: #e4800d;
$errorLow: #03aa03;

.turtas-pastaba-error {
  color: $errorHigh;
}
.turtas-pastaba-warning {
  color: $errorMiddle;
}
.turtas-expanded {
  box-shadow: none;
  background: rgb(248, 248, 248);
}
.plotas-matas {
  color: rgba(0, 0, 0, 0.87);
}
.plotas-pazymetas {
  margin-top: 25px;
  margin-left: 15px;
}

.linkas {
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}
</style>
